import React, { useState, useEffect } from 'react';
import { trailUpdate, TRAIL_MODULES } from '../../utils/activityTrail';
import {
    Card, Select, Button, Input, InputNumber, Row, Col,
    message, Table, Tag, Divider, Statistic,
    Descriptions, Progress, Alert
} from 'antd';
import {
    EditOutlined, SaveOutlined, HistoryOutlined,
    StarFilled, StarOutlined, ReloadOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import moment from 'moment';

const { Option } = Select;

const SERVICES = [
    'OPD','ANC','ARV / HIV','TB Treatment','MCH / Maternity','Immunisation',
    'Laboratory','Radiology / X-Ray','Pharmacy','Emergency / Casualty',
    'Inpatient Wards','Surgery / Theatre','Mental Health','Dental',
    'Nutrition','Physiotherapy','Palliative Care',
];

// Helper to get service value from services_offered object or legacy array
const getServiceVal = (servicesOffered, service) => {
    if (!servicesOffered) return '';
    if (Array.isArray(servicesOffered)) {
        return servicesOffered.includes(service) ? 'Yes' : '';
    }
    return servicesOffered[service] || '';
};
const OWNERSHIP_TYPES = ['Government','UEDF','Mission','Private','NGO','Other'];
const ACCRED_STATUSES = ['Not Accredited','ISO 9001:2015 Certified','ISO 15189 Certified',
    'JCI Accredited','QoC Baseline Complete','QoC Accreditation In Progress'];

const RATING = [
    { min:98, label:'Outstanding',          stars:5, color:'#1b5e20' },
    { min:95, label:'Excellent',            stars:4, color:'#1565c0' },
    { min:90, label:'Good',                 stars:3, color:'#2e7d32' },
    { min:85, label:'Fair',                 stars:2, color:'#f9a825' },
    { min:70, label:'Requires Improvement', stars:1, color:'#e64a19' },
    { min:0,  label:'Inadequate',           stars:0, color:'#b71c1c' },
];
const getRating = s => RATING.find(r => s >= r.min) || RATING[RATING.length-1];

function Stars({ count, size=12 }) {
    return <span>{[1,2,3,4,5].map(i => (
        i <= count
            ? <StarFilled  key={i} style={{ color:'#f9a825', fontSize:size }}/>
            : <StarOutlined key={i} style={{ color:'#e0e0e0', fontSize:size }}/>
    ))}</span>;
}

export default function QoCFacilityProfile() {
    const [facilities, setFacilities] = useState([]);
    const [selected, setSelected]     = useState(null);
    const [profile, setProfile]       = useState(null);
    const [editing, setEditing]       = useState(false);
    const [form, setForm]             = useState({});
    const [loading, setLoading]       = useState(false);
    const [saving, setSaving]         = useState(false);

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r =>
            setFacilities(r.data?.result?.data || []));
    }, []);

    useEffect(() => {
        if (selected) loadProfile();
    }, [selected]); // eslint-disable-line

    const loadProfile = async () => {
        setLoading(true);
        try {
            const res = await QoCService.getFacilityProfile(selected);
            const p   = res.data?.data || {};
            setProfile(p);
            setForm({
                ownership_type:       p.ownership_type || 'Government',
                catchment_population: p.catchment_population || '',
                num_beds:             p.num_beds || '',
                services_offered:     Array.isArray(p.services_offered) ? p.services_offered : [],
                implementing_partners:p.implementing_partners || '',
                staffing_approved:    p.staffing_approved || '',
                staffing_actual:      p.staffing_actual || '',
                accreditation_status: p.accreditation_status || 'Not Accredited',
            });
        } catch { message.error('Failed to load profile'); }
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await QoCService.saveFacilityProfile(selected, form);
    
            const facilityName = facilities.find(f => f.id === selected)?.business_name || String(selected);
            trailUpdate(TRAIL_MODULES.FACILITIES, facilityName, selected, {
                ownership_type:       form.ownership_type,
                accreditation_status: form.accreditation_status,
                catchment_population: form.catchment_population,
                num_beds:             form.num_beds,
            });
            message.success('Facility profile updated');
            setEditing(false);
            loadProfile();
        } catch { message.error('Save failed'); }
        finally { setSaving(false); }
    };

    const histCols = [
        { title:'Audit Date', dataIndex:'assessment_date', width:110,
          render:d => moment(d).format('DD MMM YYYY') },
        { title:'Clauses', dataIndex:'clauses', width:200,
          render:v => v ? v.split(',').map(c => (
            <Tag key={c.trim()} style={{ fontSize:9, margin:1 }}>{c.trim()}</Tag>
          )) : '—' },
        { title:'Score', width:90, dataIndex:'overall_percentage',
          render:s => {
            const r = getRating(parseFloat(s||0));
            return <span style={{ fontWeight:900, color:r.color }}>{parseFloat(s||0).toFixed(1)}%</span>;
          }},
        { title:'Rating', width:130,
          render:(_,row) => {
            const r = getRating(parseFloat(row.overall_percentage||0));
            return <span style={{ color:r.color, fontWeight:700, fontSize:11 }}>
                <Stars count={r.stars} size={10}/> {r.label}
            </span>;
          }},
        { title:'Assessor', dataIndex:'assessor_name', width:130 },
        { title:'Status', dataIndex:'status', width:90,
          render:s => <Tag color={s==='approved'?'green':'blue'} style={{ fontSize:10 }}>{s}</Tag> },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Facility Profile</h2>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Extended facility information that enriches the narrative audit report
                    </div>
                </div>
            </div>

            <div className="page-pad">
                <Card size="small" style={{ marginBottom:16 }}>
                    <Row gutter={12} align="middle">
                        <Col span={10}>
                            <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>
                                Select Facility
                            </label>
                            <Select showSearch optionFilterProp="children"
                                placeholder="Search facility..." style={{ width:'100%' }}
                                value={selected} onChange={v => { setSelected(v); setEditing(false); }}>
                                {facilities.map(f => (
                                    <Option key={f.id} value={f.id}>{f.business_name}</Option>
                                ))}
                            </Select>
                        </Col>
                        {profile && (
                            <Col span={14} style={{ display:'flex', gap:12, alignItems:'center', paddingTop:20 }}>
                                {profile.last_audit_score && (() => {
                                    const r = getRating(parseFloat(profile.last_audit_score));
                                    return (
                                        <div style={{ background:`${r.color}10`, border:`1px solid ${r.color}30`,
                                            borderRadius:8, padding:'6px 12px' }}>
                                            <div style={{ fontSize:9, color:'#888', fontWeight:700 }}>LAST AUDIT</div>
                                            <div style={{ fontWeight:900, fontSize:16, color:r.color }}>
                                                {parseFloat(profile.last_audit_score).toFixed(1)}%
                                            </div>
                                            <Stars count={r.stars} size={11}/>
                                        </div>
                                    );
                                })()}
                                {profile.cap_summary && (
                                    <div style={{ background:'#fff7e6', border:'1px solid #ffd591',
                                        borderRadius:8, padding:'6px 12px' }}>
                                        <div style={{ fontSize:9, color:'#888', fontWeight:700 }}>OPEN CAPs</div>
                                        <div style={{ fontWeight:900, fontSize:16,
                                            color: (profile.cap_summary.open + profile.cap_summary.overdue) > 0 ? '#f5222d' : '#52c41a' }}>
                                            {(profile.cap_summary.open||0) + (profile.cap_summary.overdue||0)}
                                        </div>
                                        <div style={{ fontSize:9, color:'#888' }}>
                                            {profile.cap_summary.closed||0} closed
                                        </div>
                                    </div>
                                )}
                            </Col>
                        )}
                    </Row>
                </Card>

                {profile && (
                    <>
                    {/* Profile details */}
                    <Card size="small" style={{ marginBottom:16 }}
                        title={<span>📋 Facility Details</span>}
                        extra={
                            editing
                                ? <Button type="primary" icon={<SaveOutlined/>}
                                    loading={saving} onClick={handleSave}
                                    style={{ background:'#52c41a', borderColor:'#52c41a' }}>
                                    Save Profile
                                  </Button>
                                : <Button icon={<EditOutlined/>} onClick={() => setEditing(true)}>
                                    Edit Profile
                                  </Button>
                        }>
                        {!editing ? (
                            <Descriptions column={3} size="small" bordered>
                                <Descriptions.Item label="Facility Name" span={2}>
                                    <strong>{profile.business_name}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Level">{profile.site_type || '—'}</Descriptions.Item>
                                <Descriptions.Item label="Region">{profile.located_region || '—'}</Descriptions.Item>
                                <Descriptions.Item label="Ownership">{profile.ownership_type || '—'}</Descriptions.Item>
                                <Descriptions.Item label="Accreditation">{profile.accreditation_status || '—'}</Descriptions.Item>
                                <Descriptions.Item label="Catchment Population">
                                    {profile.catchment_population ? profile.catchment_population.toLocaleString() : '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Bed Capacity">{profile.num_beds || '—'}</Descriptions.Item>
                                <Descriptions.Item label="Staffing (Approved / Actual)">
                                    {profile.staffing_approved || '—'} / {profile.staffing_actual || '—'}
                                    {profile.staffing_approved && profile.staffing_actual && (
                                        <span style={{ marginLeft:8, color: profile.staffing_actual/profile.staffing_approved < 0.7 ? '#f5222d' : '#52c41a', fontSize:11 }}>
                                            ({Math.round(profile.staffing_actual/profile.staffing_approved*100)}% filled)
                                        </span>
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Implementing Partners" span={3}>
                                    {profile.implementing_partners || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Services Offered" span={3}>
                                    {(() => {
                                        const so = profile.services_offered;
                                        if (!so) return '—';
                                        if (Array.isArray(so)) {
                                            return so.length > 0
                                                ? so.map(s => <Tag key={s} color="green" style={{ margin:2 }}>{s}</Tag>)
                                                : '—';
                                        }
                                        const yes = SERVICES.filter(s => so[s] === 'Yes');
                                        const no  = SERVICES.filter(s => so[s] === 'No');
                                        return (
                                            <div>
                                                {yes.map(s => <Tag key={s} color="success" style={{ margin:2 }}>{s}</Tag>)}
                                                {no.map(s  => <Tag key={s} color="error"   style={{ margin:2 }}>✗ {s}</Tag>)}
                                                {yes.length === 0 && no.length === 0 && '—'}
                                            </div>
                                        );
                                    })()}
                                </Descriptions.Item>
                            </Descriptions>
                        ) : (
                            <Row gutter={12}>
                                <Col span={8}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Ownership Type</label>
                                    <Select style={{ width:'100%' }} value={form.ownership_type}
                                        onChange={v => setForm(f => ({ ...f, ownership_type:v }))}>
                                        {OWNERSHIP_TYPES.map(o => <Option key={o} value={o}>{o}</Option>)}
                                    </Select>
                                </Col>
                                <Col span={8}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Accreditation Status</label>
                                    <Select style={{ width:'100%' }} value={form.accreditation_status}
                                        onChange={v => setForm(f => ({ ...f, accreditation_status:v }))}>
                                        {ACCRED_STATUSES.map(s => <Option key={s} value={s}>{s}</Option>)}
                                    </Select>
                                </Col>
                                <Col span={8}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Catchment Population</label>
                                    <InputNumber style={{ width:'100%' }} min={0}
                                        value={form.catchment_population}
                                        onChange={v => setForm(f => ({ ...f, catchment_population:v }))}
                                        formatter={v => v ? parseInt(v).toLocaleString() : ''}/>
                                </Col>
                                <Col span={8} style={{ marginTop:10 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Bed Capacity</label>
                                    <InputNumber style={{ width:'100%' }} min={0}
                                        value={form.num_beds}
                                        onChange={v => setForm(f => ({ ...f, num_beds:v }))}/>
                                </Col>
                                <Col span={8} style={{ marginTop:10 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Approved Establishment</label>
                                    <InputNumber style={{ width:'100%' }} min={0}
                                        value={form.staffing_approved}
                                        onChange={v => setForm(f => ({ ...f, staffing_approved:v }))}/>
                                </Col>
                                <Col span={8} style={{ marginTop:10 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Actual Staff (Filled)</label>
                                    <InputNumber style={{ width:'100%' }} min={0}
                                        value={form.staffing_actual}
                                        onChange={v => setForm(f => ({ ...f, staffing_actual:v }))}/>
                                </Col>
                                <Col span={24} style={{ marginTop:10 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Implementing Partners</label>
                                    <Input value={form.implementing_partners}
                                        onChange={e => setForm(f => ({ ...f, implementing_partners:e.target.value }))}
                                        placeholder="e.g. MSF, UNICEF, PEPFAR"/>
                                </Col>
                                <Col span={24} style={{ marginTop:10 }}>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:8 }}>
                                        Services Offered
                                    </label>
                                    <div style={{ border:'1px solid #f0f0f0', borderRadius:8, overflow:'hidden' }}>
                                        {/* Header */}
                                        <div style={{ display:'grid', gridTemplateColumns:'1fr 80px 80px 80px',
                                            background:'#f5f5f5', padding:'6px 12px',
                                            fontSize:11, fontWeight:700, color:'#666' }}>
                                            <span>Service</span>
                                            <span style={{ textAlign:'center', color:'#52c41a' }}>Yes</span>
                                            <span style={{ textAlign:'center', color:'#f5222d' }}>No</span>
                                            <span style={{ textAlign:'center', color:'#888' }}>N/A</span>
                                        </div>
                                        {SERVICES.map((s, i) => {
                                            const val = (form.services_offered && !Array.isArray(form.services_offered))
                                                ? (form.services_offered[s] || '')
                                                : (Array.isArray(form.services_offered) && form.services_offered.includes(s) ? 'Yes' : '');
                                            const setVal = (v) => setForm(f => ({
                                                ...f,
                                                services_offered: {
                                                    ...(Array.isArray(f.services_offered)
                                                        ? Object.fromEntries(f.services_offered.map(x => [x,'Yes']))
                                                        : (f.services_offered || {})),
                                                    [s]: v,
                                                },
                                            }));
                                            return (
                                                <div key={s} style={{
                                                    display:'grid', gridTemplateColumns:'1fr 80px 80px 80px',
                                                    padding:'7px 12px', alignItems:'center',
                                                    background: i%2 ? '#fafafa' : '#fff',
                                                    borderTop: i>0 ? '1px solid #f0f0f0' : 'none',
                                                }}>
                                                    <span style={{ fontSize:12, fontWeight:500 }}>{s}</span>
                                                    {['Yes','No','N/A'].map(opt => (
                                                        <div key={opt} style={{ textAlign:'center' }}>
                                                            <div onClick={() => setVal(val===opt ? '' : opt)} style={{
                                                                width:22, height:22, borderRadius:'50%',
                                                                border:`2px solid ${
                                                                    val===opt
                                                                        ? (opt==='Yes'?'#52c41a':opt==='No'?'#f5222d':'#888')
                                                                        : '#d9d9d9'
                                                                }`,
                                                                background: val===opt
                                                                    ? (opt==='Yes'?'#52c41a':opt==='No'?'#f5222d':'#888')
                                                                    : 'transparent',
                                                                margin:'0 auto', cursor:'pointer',
                                                                display:'flex', alignItems:'center', justifyContent:'center',
                                                            }}>
                                                                {val===opt && <div style={{ width:10, height:10, borderRadius:'50%', background:'#fff' }}/>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </Card>

                    {/* Audit history */}
                    {profile.audit_history?.length > 0 && (
                        <Card size="small"
                            title={<span><HistoryOutlined style={{ marginRight:6 }}/>
                                Audit History ({profile.audit_history.length} audits)</span>}>
                            <Table columns={histCols}
                                dataSource={profile.audit_history}
                                rowKey="id" size="small"
                                pagination={{ pageSize:10, size:'small' }}/>
                        </Card>
                    )}
                    </>
                )}

                {!selected && (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa' }}>
                        <div style={{ fontSize:48 }}>🏥</div>
                        <div style={{ fontSize:14, marginTop:8 }}>
                            Select a facility above to view and edit its profile
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}